import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { ReactComponent as UserProfile } from "../../assets/icons/user.svg";
import AuthenticationService from "../../_services/AuthenticationService";
import ApiService from "../../_services/ApiService";
import "./timetable-new.css";

interface TimetableNewProps extends RouteComponentProps {}

interface ActivitySlot {
  activityId: number;
  startTime: string;
  endTime: string;
  batchName: string;
  batchId: string;
  courseName: string;
  topicName: string;
  date: string;
  dayOfWeek: string;
}

export const TimetableNew: React.FC<TimetableNewProps> = (props) => {
  const [activities, setActivities] = useState<ActivitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUser = AuthenticationService.currentUserValue;
  const userName = currentUser?.enquiryName || currentUser?.userName || "User";
  const instructorId = (currentUser as any)?.instructorId;
  const userProfilePic = (currentUser as any)?.photoFile || null;

  // Search filters
  const today = new Date();
  const defaultEndDate = new Date();
  defaultEndDate.setDate(today.getDate() + 7);

  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);

  const goBack = () => {
    props.history.goBack();
  };

  useEffect(() => {
    fetchTimetable();
  }, []);

  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchTimetable = async () => {
    try {
      setLoading(true);
      setError(null);

      const timetableData = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        instructorId: instructorId?.toString() || "202",
        batchId: "-1",
      };

      const response = await ApiService.getInstructorTimetable(timetableData);

      if (response.data?.status === 1 && response.data?.data?.timeTableDataBeans) {
        const timeTableDataBeans = response.data.data.timeTableDataBeans;
        const allActivities: ActivitySlot[] = [];

        timeTableDataBeans.forEach((dayData: any) => {
          if (dayData.activitySlotDataBeans && Array.isArray(dayData.activitySlotDataBeans)) {
            dayData.activitySlotDataBeans.forEach((slot: any) => {
              allActivities.push({
                activityId: parseInt(slot.activitySlotId),
                startTime: slot.startTime,
                endTime: slot.endTime,
                batchName: slot.batchName || slot.shortBatchName,
                batchId: slot.batchId,
                courseName: slot.topicName,
                topicName: slot.topicName,
                date: slot.date || dayData.dateString,
                dayOfWeek: dayData.dayOfweek,
              });
            });
          }
        });

        setActivities(allActivities);
      } else {
        setError("Failed to load timetable data");
      }
    } catch (err) {
      console.error("Error fetching timetable:", err);
      setError("An error occurred while loading the timetable");
    } finally {
      setLoading(false);
    }
  };

  const groupActivitiesByDate = () => {
    const grouped: { [key: string]: ActivitySlot[] } = {};

    activities.forEach((activity) => {
      const date = activity.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(activity);
    });

    return grouped;
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const parts = time.split(":");
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes} ${ampm}`;
    }
    return time;
  };

  const formatDateDisplay = (dateStr: string, dayOfWeek: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0]);
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      const month = monthNames[parseInt(parts[1]) - 1];
      const year = parts[2];

      return { day, dayOfWeek, month, year };
    }
    return null;
  };

  const groupedActivities = groupActivitiesByDate();
  const sortedDates = Object.keys(groupedActivities).sort((a, b) => {
    const [dayA, monthA, yearA] = a.split("/").map(Number);
    const [dayB, monthB, yearB] = b.split("/").map(Number);
    const dateA = new Date(yearA, monthA - 1, dayA);
    const dateB = new Date(yearB, monthB - 1, dayB);
    return dateA.getTime() - dateB.getTime();
  });

  if (loading) {
    return (
      <div className="modern-home">
        <div className="home-nav">
          <div className="home-nav-content">
            <button onClick={goBack} className="back-button">
              ← Back
            </button>
            <div className="brand">
              <span className="brand-icon">📅</span>
              <span className="brand-text">My Timetable</span>
            </div>
            <div className="user-info" onClick={() => props.history.push("/profile")}>
              {userProfilePic ? (
                <img
                  src={userProfilePic}
                  alt={userName}
                  className="user-avatar"
                  style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <UserProfile className="user-avatar" />
              )}
              <span className="user-name">{userName}</span>
            </div>
          </div>
        </div>
        <div className="modern-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading timetable...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-home">
        <div className="home-nav">
          <div className="home-nav-content">
            <button onClick={goBack} className="back-button">
              ← Back
            </button>
            <div className="brand">
              <span className="brand-icon">📅</span>
              <span className="brand-text">My Timetable</span>
            </div>
            <div className="user-info" onClick={() => props.history.push("/profile")}>
              {userProfilePic ? (
                <img
                  src={userProfilePic}
                  alt={userName}
                  className="user-avatar"
                  style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <UserProfile className="user-avatar" />
              )}
              <span className="user-name">{userName}</span>
            </div>
          </div>
        </div>
        <div className="modern-content">
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button onClick={fetchTimetable} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-home">
      <div className="home-nav">
        <div className="home-nav-content">
          <button onClick={goBack} className="back-button">
            ← Back
          </button>
          <div className="brand">
            <span className="brand-icon">📅</span>
            <span className="brand-text">My Timetable</span>
          </div>
          <div className="user-info" onClick={() => props.history.push("/profile")}>
            {userProfilePic ? (
              <img
                src={userProfilePic}
                alt={userName}
                className="user-avatar"
                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <UserProfile className="user-avatar" />
            )}
            <span className="user-name">{userName}</span>
          </div>
        </div>
      </div>

      <div className="modern-content">
        {/* Search Section */}
        <div className="search-section">
          <h2 className="search-title">INSTRUCTOR TIME TABLE SEARCH</h2>
          <div className="search-filters">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                className="date-input"
                value={formatDateForInput(startDate)}
                onChange={(e) => setStartDate(new Date(e.target.value))}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                className="date-input"
                value={formatDateForInput(endDate)}
                onChange={(e) => setEndDate(new Date(e.target.value))}
              />
            </div>
            <div className="filter-group filter-group-button">
              <button className="search-button" onClick={fetchTimetable} disabled={loading}>
                {loading ? "Searching..." : "Search 🔍"}
              </button>
            </div>
          </div>
        </div>

        {/* Activities Section */}
        <div className="activities-section">
          <h2 className="activities-title">ACTIVITIES</h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading timetable...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button onClick={fetchTimetable} className="retry-button">
              Retry
            </button>
          </div>
        ) : activities.length === 0 ? (
          <div className="no-activities">
            <span className="no-activities-icon">📭</span>
            <p>No activities scheduled for the next 7 days</p>
          </div>
        ) : (
          <div className="activities-container">
            {sortedDates.map((dateStr) => {
              const dateDisplay = formatDateDisplay(dateStr, groupedActivities[dateStr][0]?.dayOfWeek);
              if (!dateDisplay) return null;

              return (
                <div key={dateStr} className="date-section">
                  <div className="date-header">
                    <div className="date-badge">
                      <span className="date-day">{dateDisplay.day}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span className="date-weekday">{dateDisplay.dayOfWeek}</span>
                        <span className="date-month">{dateDisplay.month}, {dateDisplay.year}</span>
                      </div>
                    </div>
                  </div>

                  <div className="activities-list">
                    {groupedActivities[dateStr].map((activity, index) => (
                      <div key={index} className="activity-card">
                        <div className="activity-time">
                          <span className="time-label">
                            {formatTime(activity.startTime)} - {formatTime(activity.endTime)}
                          </span>
                        </div>
                        <div className="activity-details">
                          <div className="activity-row">
                            <span className="activity-label">BATCH</span>
                            <span className="activity-batch">{activity.batchName}</span>
                          </div>
                          <div className="activity-row">
                            <span className="activity-label">TOPIC</span>
                            <span className="activity-course">{activity.courseName}</span>
                          </div>
                          <div className="activity-row">
                            <span className="activity-label">ACTION</span>
                            <button
                              className="action-button attendance-button"
                              onClick={() => props.history.push(`/attendance/batches/${activity.batchId}`)}
                            >
                              Attendance
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
